import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { navLinks, categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const handleCategoryClick = (url: string) => {
    closeMobile();
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 text-foreground-950 no-underline" onClick={closeMobile}>
            <span className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
              Kakobuy<span className="text-primary-500">Polska</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.href.startsWith('/') ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer no-underline"
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer no-underline"
                >
                  {link.name}
                </a>
              )
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer no-underline"
            >
              <i className="ri-home-line"></i>
              Strona główna
            </Link>
            <Link
              to="/blog/kakobuy-spreadsheet-polska-przewodnik"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-secondary-100 text-secondary-800 rounded-full text-sm font-semibold hover:bg-secondary-200 transition-all whitespace-nowrap cursor-pointer no-underline"
            >
              <i className="ri-book-open-line"></i>
              Poradnik zakupów
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer no-underline"
            >
              <i className="ri-external-link-line"></i>
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer no-underline"
            >
              <i className="ri-home-line text-lg text-primary-500"></i>
              Strona główna
            </Link>

            <Link
              to="/blog/kakobuy-spreadsheet-polska-przewodnik"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer no-underline"
            >
              <i className="ri-book-open-line text-lg text-secondary-600"></i>
              Poradnik zakupów
            </Link>

            <Link
              to="/blog/kakobuy-bezpieczenstwo"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer no-underline"
            >
              <i className="ri-shield-check-line text-lg text-accent-600"></i>
              Bezpieczeństwo Kakobuy
            </Link>

            <div className="border-t border-background-200/70 my-2"></div>

            <p className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
              Kategorie produktów
            </p>

            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryClick(cat.url)}
                  className="flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer text-left"
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-secondary-100 rounded-md flex-shrink-0">
                    <i className={`${cat.icon} text-xs text-secondary-600`}></i>
                  </span>
                  <span className="leading-tight">{cat.name}</span>
                </button>
              ))}
            </div>

            <div className="border-t border-background-200/70 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer mt-1 no-underline"
            >
              <i className="ri-external-link-line"></i>
              Kakobuy Spreadsheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}